import { useCallback } from 'react'
import { collection, query, where, doc, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { toast } from 'sonner'
import { addDoc, getDoc, getDocs, getDocsCacheFirst, setDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track, type AnalyticsEvents } from '../lib/analytics'
import { unlinkGroupEvent } from '../lib/groupUnlink'
import { toastGroupAlreadyLinked, toastGroupLinked } from '../lib/groupLinkToasts'
import { canLinkDirectly, canUnlinkGroupEvent, groupEventRequestId } from '@shared/groupRoles'
import type { Group, GroupEvent, GroupEventRequest } from '../types'
import { fbt } from 'fbtee'

const EMPTY_LINKED: Group[] = []

export type GroupEventLinkEntryPoint = AnalyticsEvents['group_event_requested']['entry_point']

/**
 * What a link attempt did. 'requested' means the group requires approval and
 * the viewer isn't an admin, so it became a suggestion to the group's admins.
 */
export type GroupLinkOutcome = 'linked' | 'already_linked' | 'requested' | 'already_requested' | 'skipped'

/**
 * Which of this event's linked groups the caller is in, read from Firestore's
 * local cache — for the post-RSVP prompt, which needs the answer once per RSVP
 * and shouldn't hold a listener open across the whole app to get it. Cache-only,
 * so it never blocks the toast on the network; a cold cache reads as "nothing
 * linked", which at worst offers a link the picker then shows as already made.
 */
export async function getLinkedGroupIds(eventId: string, myGroupIds: string[]): Promise<Set<string>> {
  if (myGroupIds.length === 0) return new Set()
  const snap = await getDocsCacheFirst(
    query(collection(db, 'groupEvents'), where('eventId', '==', eventId)) as Query<GroupEvent>,
  )
  const mine = new Set(myGroupIds)
  return new Set(snap.docs.map(d => d.data().groupId).filter(id => mine.has(id)))
}

/**
 * Link a group to an event without holding a subscription to the event's
 * groupEvents — for the post-RSVP prompt, which links a single group straight
 * from a toast and would otherwise pay for a listener on every RSVP. Resolves
 * to the new link's id, or null when the link already existed.
 */
export async function linkGroupToEvent(
  groupId: string,
  eventId: string,
  userId: string,
  entryPoint: AnalyticsEvents['group_event_linked']['entry_point'],
): Promise<string | null> {
  return runMutation(fbt("Couldn't link group", 'Error toast when linking a group to an event fails'), async () => {
    const existing = await getDocs(
      query(
        collection(db, 'groupEvents'),
        where('eventId', '==', eventId),
        where('groupId', '==', groupId),
      ) as Query<GroupEvent>,
    )
    if (!existing.empty) return null
    const ref = await addDoc(collection(db, 'groupEvents'), {
      groupId,
      eventId,
      createdBy: userId,
      createdAt: serverTimestamp(),
    })
    track('group_event_linked', { group_id: groupId, event_id: eventId, entry_point: entryPoint })
    return ref.id
  })
}

/**
 * Suggest an event to a group's admins. One suggestion per group + event: a
 * repeat tap while it's pending says so, and a declined one is suggested afresh.
 */
export async function requestGroupEventLink(
  group: Pick<Group, 'id' | 'name'>,
  eventId: string,
  userId: string,
  entryPoint: GroupEventLinkEntryPoint,
): Promise<'requested' | 'already_requested'> {
  return runMutation(fbt("Couldn't send suggestion", 'Error toast when suggesting an event to a group fails'), async () => {
    const ref = doc(db, 'groupEventRequests', groupEventRequestId(group.id, eventId))
    const existing = await getDoc(ref)
    if (existing.exists() && (existing.data() as GroupEventRequest).status !== 'rejected') {
      toast(fbt(
        'Already suggested to the admins of ' + fbt.param('group', group.name),
        'Toast when the user suggests an event that is already waiting for a group admin',
      ))
      return 'already_requested'
    }
    await setDoc(ref, { groupId: group.id, eventId, requestedBy: userId, status: 'pending', createdAt: serverTimestamp() })
    track('group_event_requested', { group_id: group.id, event_id: eventId, entry_point: entryPoint })
    toast.success(
      fbt(
        'Suggested to the admins of ' + fbt.param('group', group.name),
        'Toast after the user suggests linking an event to a group that requires admin approval',
      ),
      {
        description: fbt(
          'It shows up in the group once an admin approves it.',
          'Toast body after suggesting an event to a group that requires admin approval',
        ),
      },
    )
    return 'requested'
  })
}

async function linkDirectly(
  group: Pick<Group, 'id' | 'name'>,
  eventId: string,
  userId: string,
  entryPoint: GroupEventLinkEntryPoint,
): Promise<GroupLinkOutcome> {
  const id = await linkGroupToEvent(group.id, eventId, userId, entryPoint)
  if (id) toastGroupLinked(group.name, { id, groupId: group.id, eventId })
  else toastGroupAlreadyLinked(group.name)
  return id ? 'linked' : 'already_linked'
}

/**
 * Link directly when the viewer may, otherwise suggest to the admins. Owns the
 * outcome's toast ("Linked" with Undo, "already linked", or a suggestion toast).
 */
export async function linkOrRequestGroupEvent(
  group: Group,
  eventId: string,
  userId: string,
  entryPoint: GroupEventLinkEntryPoint,
): Promise<GroupLinkOutcome> {
  if (!canLinkDirectly(group, userId)) return requestGroupEventLink(group, eventId, userId, entryPoint)
  return linkDirectly(group, eventId, userId, entryPoint)
}

export function useGroupEvents(eventId: string, userId: string | null, userGroups: Group[]) {
  const ref = userId
    ? (query(collection(db, 'groupEvents'), where('eventId', '==', eventId)) as Query<GroupEvent>)
    : null
  const { data, loading, error } = useFirestoreCollection<GroupEvent>(ref)

  const groupEvents = data
  const linkedGroups = userId
    ? userGroups.filter(g => groupEvents.some(ge => ge.groupId === g.id))
    : EMPTY_LINKED

  /**
   * `nameHint` covers a group created a moment ago, which hasn't reached the
   * groups subscription yet. Its creator is the viewer, so it links directly.
   */
  const linkGroup = useCallback(
    async (groupId: string, entryPoint: GroupEventLinkEntryPoint = 'event_page', nameHint = ''): Promise<GroupLinkOutcome> => {
      if (!userId) return 'skipped'
      const group = userGroups.find(g => g.id === groupId)
      if (groupEvents.some(ge => ge.groupId === groupId)) {
        toastGroupAlreadyLinked(group?.name ?? nameHint)
        return 'already_linked'
      }
      if (!group) return linkDirectly({ id: groupId, name: nameHint }, eventId, userId, entryPoint)
      return linkOrRequestGroupEvent(group, eventId, userId, entryPoint)
    },
    [userId, eventId, groupEvents, userGroups],
  )

  const unlinkGroup = useCallback(
    async (groupId: string) => {
      const ge = groupEvents.find(g => g.groupId === groupId)
      if (ge) await unlinkGroupEvent(ge, 'event_page')
    },
    [groupEvents],
  )

  // Admins, or whoever made the link. The callable also refuses the latter once
  // other members reacted, which this list doesn't know about.
  const canUnlinkGroup = useCallback(
    (groupId: string) => {
      const ge = groupEvents.find(g => g.groupId === groupId)
      const group = userGroups.find(g => g.id === groupId)
      return !!ge && !!group && canUnlinkGroupEvent(group, ge, userId, false)
    },
    [groupEvents, userGroups, userId],
  )

  return { groupEvents, linkedGroups, loading, error, linkGroup, unlinkGroup, canUnlinkGroup }
}
